import React from "react";
import styled from "styled-components";

export type BannerType = "info" | "warn" | "error";

type SystemBannerProps = {
  type?: BannerType;
  message?: string;
  onClose: () => void;
};

type ThemeTokens = {
  bg: string;
  border: string;
  iconColor: string;
  textColor: string;
  strongColor: string;
  btnBorder: string;
  btnColor: string;
  btnHoverBg: string;
  btnHoverBorder: string;
  btnHoverColor: string;
  btnActiveBg: string;
  btnFocusShadow: string;
};

const THEME: Record<BannerType, ThemeTokens> = {
  info: {
    bg: "linear-gradient(90deg, #f0fff4 0%, #e6ffee 100%)",
    border: "rgba(56, 158, 85, 0.28)",
    iconColor: "#389e55",
    textColor: "#1b5e36",
    strongColor: "#0f3d22",
    btnBorder: "rgba(56, 158, 85, 0.45)",
    btnColor: "#2d8049",
    btnHoverBg: "rgba(56, 158, 85, 0.14)",
    btnHoverBorder: "#389e55",
    btnHoverColor: "#0f3d22",
    btnActiveBg: "rgba(56, 158, 85, 0.24)",
    btnFocusShadow: "rgba(56, 158, 85, 0.3)",
  },
  warn: {
    bg: "linear-gradient(90deg, #fffbe6 0%, #fff7d6 100%)",
    border: "rgba(212, 136, 6, 0.25)",
    iconColor: "#d48806",
    textColor: "#7c4a00",
    strongColor: "#613400",
    btnBorder: "rgba(212, 136, 6, 0.4)",
    btnColor: "#ad6800",
    btnHoverBg: "rgba(212, 136, 6, 0.14)",
    btnHoverBorder: "#d48806",
    btnHoverColor: "#613400",
    btnActiveBg: "rgba(212, 136, 6, 0.24)",
    btnFocusShadow: "rgba(212, 136, 6, 0.3)",
  },
  error: {
    bg: "linear-gradient(90deg, #fff1f0 0%, #ffe6e4 100%)",
    border: "rgba(207, 19, 34, 0.22)",
    iconColor: "#cf1322",
    textColor: "#7a1218",
    strongColor: "#5c0d14",
    btnBorder: "rgba(207, 19, 34, 0.38)",
    btnColor: "#a8071a",
    btnHoverBg: "rgba(207, 19, 34, 0.12)",
    btnHoverBorder: "#cf1322",
    btnHoverColor: "#5c0d14",
    btnActiveBg: "rgba(207, 19, 34, 0.22)",
    btnFocusShadow: "rgba(207, 19, 34, 0.28)",
  },
};

const BannerShell = styled.section<{ $theme: ThemeTokens }>`
  position: relative;
  z-index: 9;
  padding: 8px 20px;
  background: ${({ $theme }) => $theme.bg};
  border-bottom: 1px solid ${({ $theme }) => $theme.border};
  transition: background 0.2s, border-color 0.2s;
`;

const BannerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 575px) {
    align-items: flex-start;
  }
`;

const IconWrap = styled.span<{ $theme: ThemeTokens }>`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  color: ${({ $theme }) => $theme.iconColor};
  font-size: 16px;
  margin-top: 1px;

  @media (max-width: 575px) {
    display: none;
  }
`;

const BannerText = styled.span<{ $theme: ThemeTokens }>`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  color: ${({ $theme }) => $theme.textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  strong {
    font-weight: 700;
    margin-right: 6px;
    color: ${({ $theme }) => $theme.strongColor};
  }

  @media (max-width: 575px) {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    line-height: 1.5;
  }
`;

const CloseButton = styled.button<{ $theme: ThemeTokens }>`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid ${({ $theme }) => $theme.btnBorder};
  border-radius: 5px;
  background: transparent;
  color: ${({ $theme }) => $theme.btnColor};
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  svg {
    display: block;
  }

  &:hover {
    background: ${({ $theme }) => $theme.btnHoverBg};
    border-color: ${({ $theme }) => $theme.btnHoverBorder};
    color: ${({ $theme }) => $theme.btnHoverColor};
  }

  &:active {
    background: ${({ $theme }) => $theme.btnActiveBg};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ $theme }) => $theme.btnFocusShadow};
  }
`;

const CloseIcon: React.FC = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1L8 8M8 1L1 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const SystemBanner: React.FC<SystemBannerProps> = ({ type, message, onClose }) => {
  const safeType: BannerType = type != null && type in THEME ? type : "warn";
  const theme = THEME[safeType];
  return (
    <BannerShell role="status" aria-live="polite" $theme={theme}>
      <BannerRow>
        <IconWrap $theme={theme}>
          <i className="icon icon-alert" />
        </IconWrap>
        <BannerText $theme={theme}>
          { message ? <strong>{message}</strong> : (
            <>
              <strong>System Maintenance:</strong>
              Some features may respond slower than usual while we update core services. Monitoring remains available throughout.
            </>
          )}
        </BannerText>
        <CloseButton
          type="button"
          onClick={onClose}
          aria-label="Close system maintenance notice"
          title="Close"
          $theme={theme}
        >
          <CloseIcon />
        </CloseButton>
      </BannerRow>
    </BannerShell>
  );
};

export default SystemBanner;
